import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Play } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { logout } from "@/lib/api";
import { useActiveChannelsNav } from "@/hooks/useActiveChannelsNav";

const PHASES = [
  {
    label: "Phase 1 - Pick a niche",
    items: [
      { to: "/niche-opportunities", label: "Niche Opportunities", icon: "★", testid: "nav-niche-opportunities", adminOnly: true },
      { to: "/top-videos-30d", label: "Top Videos (30d)", icon: "▶", testid: "nav-top-videos-30d" },
      { to: "/top-niches-30d", label: "Top Niches (30d)", icon: "◈", testid: "nav-top-niches-30d" },
      { to: "/top-revenue", label: "Top Revenue Channels", icon: "$$", testid: "nav-top-revenue" },
      { to: "/mid-revenue", label: "$50k to $100k Revenue Channels", icon: "$", testid: "nav-mid-revenue" },
      { to: "/low-revenue", label: "$10k to $50k Revenue Channels", icon: "$", testid: "nav-low-revenue" },
      { to: "/top-earners", label: "Top Earners (small/new)", icon: "$", testid: "nav-top-earners", adminOnly: true },
      { to: "/dashboard", label: "Niches", icon: "◎", testid: "nav-niches", adminOnly: true },
      { to: "/search", label: "Channel Search", icon: "⌕", testid: "nav-search" },
      { to: "/saved-niches", label: "Saved Niches", icon: "♥", testid: "nav-saved-niches" },
      { to: "/saved", label: "Saved AI Ideas", icon: "✦", testid: "nav-saved", adminOnly: true },
    ],
  },
  {
    label: "Phase 2 - Launch a channel",
    items: [
      { to: "/create-channel", label: "Create Channel", icon: "+", testid: "nav-create-channel" },
      { to: "/style-gallery", label: "Style Gallery", icon: "◆", testid: "nav-style-gallery" },
      { to: "/characters", label: "Characters", icon: "☻", testid: "nav-characters" },
      { to: "/saved-kits", label: "Saved Channel Kits", icon: "♛", testid: "nav-saved-kits" },
    ],
  },
  {
    label: "Phase 3 - My channels",
    items: [
      { to: "/active-channels", label: "Dashboard", icon: "◉", testid: "nav-active-channels" },
    ],
  },
  {
    // Phase 4 was reordered on 2026-02-06 per user brief — the Idea →
    // Story → Review → Cost path mirrors the actual workflow, so put
    // "Generate Story Ideas" first and drop the redundant top-level
    // "Storyboard" + "Auto-video library" entries (both are still
    // reachable via a channel's Dashboard).
    //
    // 2026-02-08: instead of a separate "Storyboards" sidebar entry,
    // the full storyboard library is embedded as a tab inside the
    // Content Factory page itself — see /app/frontend/src/pages/
    // ReviewApprove.jsx. This keeps the sidebar minimal while giving
    // users a single "everything content" destination.
    label: "Phase 4 - Content",
    items: [
      { to: "/generate-ideas", label: "Generate Story Ideas", icon: "✦", testid: "nav-generate-ideas" },
      { to: "/create-story", label: "Create Your Own Story", icon: "✎", testid: "nav-create-story" },
      { to: "/ideas", label: "Saved Story Ideas", icon: "★", testid: "nav-saved-story-ideas" },
      { to: "/review", label: "Content Factory", icon: "✓", testid: "nav-review-approve" },
      { to: "/cost", label: "Cost", icon: "$", testid: "nav-cost" },
    ],
  },
];

// Local-storage key for the collapsed-state map. Kept in one place
// so tests + Settings can wipe it if we ever add a "reset UI" button.
const COLLAPSED_STORAGE_KEY = "ytm.sidebar.collapsed_phases";

function loadCollapsedMap() {
  try {
    const raw = localStorage.getItem(COLLAPSED_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function NavRow({ n }) {
  return (
    <NavLink
      to={n.to}
      data-testid={n.testid}
      className={({ isActive }) =>
        `flex items-center gap-3 pl-8 pr-5 py-2 text-sm border-l-2 transition-colors ${
          isActive
            ? "border-yellow-400 bg-zinc-950 text-white"
            : "border-transparent text-zinc-200 hover:text-yellow-400 hover:bg-zinc-950"
        }`
      }
      end
    >
      <span className="font-mono text-yellow-400 w-4">{n.icon}</span>
      <span>{n.label}</span>
    </NavLink>
  );
}

export default function Sidebar() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const activeChannels = useActiveChannelsNav();

  // Per-phase collapse state, persisted to localStorage so it
  // survives reloads/nav. Key = phase.label (stable + unique).
  const [collapsed, setCollapsed] = useState(loadCollapsedMap);

  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSED_STORAGE_KEY, JSON.stringify(collapsed));
    } catch {
      /* ignore quota / private-mode errors */
    }
  }, [collapsed]);

  // If the user navigates into a route that lives inside a
  // collapsed phase, auto-expand that phase so the active row is
  // visible instead of hidden. This is a one-shot on route change
  // — we don't force it open on every render.
  useEffect(() => {
    for (const phase of PHASES) {
      if (!collapsed[phase.label]) continue;
      const hit = phase.items.some((n) => n.to === location.pathname);
      if (hit) {
        setCollapsed((prev) => ({ ...prev, [phase.label]: false }));
        break;
      }
    }
    // Intentional: run only when the URL changes.
  }, [location.pathname]);

  const togglePhase = (label) =>
    setCollapsed((prev) => ({ ...prev, [label]: !prev[label] }));

  const handleLogout = async () => {
    try { await logout(); } catch { /* ignore */ }
    setUser(null);
    navigate("/login", { replace: true });
  };

  return (
    <aside
      data-testid="sidebar"
      className="hidden md:flex w-64 flex-col border-r border-zinc-900 bg-black sticky top-0 h-screen overflow-y-auto"
    >
      <Link
        to="/dashboard"
        data-testid="sidebar-logo"
        className="px-5 py-5 border-b border-zinc-900 flex items-center gap-3 group"
      >
        <span
          aria-hidden
          className="w-9 h-9 rounded-md bg-yellow-400 grid place-items-center shadow-[0_0_24px_-8px_rgba(250,204,21,0.8)] group-hover:shadow-[0_0_28px_-4px_rgba(250,204,21,0.95)] transition-shadow flex-shrink-0"
        >
          <Play className="w-4 h-4 text-black fill-black -ml-0.5" strokeWidth={0} />
        </span>
        <div className="min-w-0 leading-tight">
          <div className="font-display font-black text-xl text-white tracking-tight">
            YT<span className="text-yellow-400">Maker</span>.AI
          </div>
          <div className="font-mono text-[9px] font-bold uppercase tracking-[0.3em] text-yellow-400 mt-0.5">
            // CONTENT FACTORY
          </div>
        </div>
      </Link>

      <nav className="flex-1 py-4">
        {/* Phase groups — each header toggles collapse */}
        {PHASES.map((phase) => {
          const visibleItems = phase.items.filter(
            (n) => !n.adminOnly || user?.is_admin
          );
          if (visibleItems.length === 0) return null;
          const isMyChannels = phase.label.startsWith("Phase 3");
          const phaseNum = phase.label.split(" ")[1];
          const isCollapsed = !!collapsed[phase.label];
          return (
            <div key={phase.label} className="mt-6">
              <button
                type="button"
                onClick={() => togglePhase(phase.label)}
                data-testid={`nav-phase-toggle-${phaseNum}`}
                aria-expanded={!isCollapsed}
                className="w-full flex items-center justify-between gap-2 px-5 mb-2 font-mono text-[11px] uppercase tracking-[0.1em] text-yellow-400 font-semibold whitespace-nowrap hover:text-yellow-300 transition-colors"
              >
                <span data-testid={`nav-phase-${phaseNum}`}>{phase.label}</span>
                <span
                  aria-hidden
                  className={`font-mono text-[10px] text-yellow-400/70 transition-transform ${
                    isCollapsed ? "-rotate-90" : ""
                  }`}
                >
                  ▾
                </span>
              </button>
              {!isCollapsed && (
                <div data-testid={`nav-phase-body-${phaseNum}`}>
                  {visibleItems.map((n) => (
                    <NavRow key={n.to} n={n} />
                  ))}
                  {/* Phase 3 · My channels — render each tracked
                      channel as a nested clickable row under
                      "Dashboard". */}
                  {isMyChannels && activeChannels.length > 0 && (
                    <div className="mt-1 mb-2" data-testid="sidebar-active-channel-list">
                      {activeChannels.map((ch) => (
                        <NavLink
                          key={ch.channel_id}
                          to={`/active-channels/${ch.channel_id}`}
                          data-testid={`sidebar-active-channel-${ch.channel_id}`}
                          className={({ isActive }) =>
                            `flex items-center gap-2 pl-12 pr-5 py-1.5 text-xs border-l-2 transition-colors ${
                              isActive
                                ? "border-yellow-400 bg-zinc-950 text-white"
                                : "border-transparent text-zinc-400 hover:text-yellow-300 hover:bg-zinc-950"
                            }`
                          }
                          title={ch.name}
                        >
                          {ch.thumbnail ? (
                            <img
                              src={ch.thumbnail}
                              alt=""
                              className="h-4 w-4 rounded-full border border-zinc-800 shrink-0"
                              referrerPolicy="no-referrer"
                            />
                          ) : (
                            <span className="h-4 w-4 rounded-full bg-zinc-800 shrink-0" />
                          )}
                          <span className="truncate">{ch.name}</span>
                        </NavLink>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {/* Utility — admin only */}
        {user?.is_admin && (
          <div className="mt-6">
            <div className="px-5 mb-2 font-mono text-[11px] uppercase tracking-[0.1em] text-yellow-400 font-semibold whitespace-nowrap">
              Utility
            </div>
            <NavRow n={{ to: "/history", label: "Scan history", icon: "⌖", testid: "nav-history" }} />
          </div>
        )}

        {user?.is_admin && (
          <div className="mt-6">
            <div className="px-5 mb-2 font-mono text-[11px] uppercase tracking-[0.1em] text-yellow-400 font-semibold whitespace-nowrap">
              Admin
            </div>
            <NavRow n={{ to: "/admin", label: "Admin", icon: "⚙", testid: "nav-admin" }} />
            <NavRow n={{ to: "/admin/render-log", label: "AI Render Log", icon: "▤", testid: "nav-render-log" }} />
          </div>
        )}

        {/* Settings — bottom of the nav, above the user card */}
        <div className="mt-6 mb-2">
          <NavRow
            n={{ to: "/settings", label: "Settings", icon: "⚙", testid: "nav-settings" }}
          />
        </div>
      </nav>

      <div className="px-5 py-4 border-t border-zinc-900">
        <div className="flex items-center gap-3 mb-3">
          {user?.picture ? (
            <img src={user.picture} alt={user.name} className="h-8 w-8 rounded-full" />
          ) : (
            <div className="h-8 w-8 rounded-full bg-zinc-800" />
          )}
          <div className="flex-1 min-w-0">
            <div className="text-xs text-white truncate" data-testid="sidebar-user-name">
              {user?.name || "Operator"}
            </div>
            <div className="text-[11px] text-zinc-500 truncate font-mono">
              {user?.email}
            </div>
          </div>
        </div>
        <button
          data-testid="logout-btn"
          onClick={handleLogout}
          className="w-full text-xs font-mono uppercase tracking-wider text-zinc-400 hover:text-yellow-400 py-2 border border-zinc-900 hover:border-zinc-700 rounded-sm transition-colors"
        >
          Log out
        </button>
      </div>
    </aside>
  );
}
